// CUSTOM: personalised-review-actions.js
// Review page actions and button management module.
// Handles all action-handling and button-management concerns for the personalised review page:
//   handleSubmitButton (edit update handler), initCheckoutButton, initAfterPayVisibility,
//   initDisclaimerVisibility, manageCheckoutButtonPosition, updateCheckoutButtonVisibility.
// Depends on: personalised-config.js, personalised-utils.js, personalised-recharge-utils.js,
//   personalised-bundle-state.js, personalised-bundle-ui.js, personalised-recharge-session.js (eventBus).

(function() {
  window.personalised = window.personalised || {};

  const reviewActions = (function() {

    // =============================================================================
    // ===== EDIT MODE ONLY — ACTION HANDLER =====
    // =============================================================================

    /**
     * [EDIT MODE ONLY] Handles the click event for the Update Subscription button.
     * Validates metadata, calls rechargeUtils.updateBundle(), resolves the Recharge portal URL,
     * and navigates to the portal on success.
     * @param {Event} event - The click event
     */
    async function handleSubmitButton(event) {
      const config = window.personalised.config;
      event.preventDefault();

      let navigating = false;

      // Verify we're in edit mode
      if (window.personalised.utils.getMode() !== config.modes.EDIT) {
        if (window.personalised?.debugMode) console.warn('Submit handler triggered outside edit mode');
        return;
      }

      // Get button reference
      const submitButton = event.currentTarget;

      // Add loading state and disable button
      if (submitButton) {
        submitButton.classList.add('loading');
        submitButton.disabled = true;
        // Show spinner for update flow
        if (window.personalised?.modalSpinner?.show) {
          window.personalised.modalSpinner.show('Loading your pack…');
        }
      }

      try {
        // Validate metadata integrity before update
        if (window.personalised.utils?.validateMetadataIntegrity) {
          const metadataValid = await window.personalised.utils.validateMetadataIntegrity();
          if (!metadataValid) {
            throw new Error('Missing critical metadata for bundle update');
          }
        }

        // Call update bundle function
        const result = await window.personalised.rechargeUtils.updateBundle();

        // Handle success
        if (result.success) {
          if (window.personalised?.debugMode) console.log('Bundle updated successfully');

          // Redirect to Recharge portal using portal-link component
          try {
            // Wait for the custom element to be defined
            customElements.whenDefined('portal-link').then(() => {
              const portalLink = document.querySelector('portal-link');
              let portalUrl = null;

              // Try multiple sources for portal URL (Shadow DOM-free fallback)
              if (portalLink && portalLink.shadowRoot) {
                const button = portalLink.shadowRoot.querySelector('a');
                if (button && button.href) {
                  portalUrl = button.href;
                }
              }

              // Fallback to data attribute
              if (!portalUrl && portalLink) {
                portalUrl = portalLink.getAttribute('data-portal-href');
              }

              // Fallback to hidden anchor
              if (!portalUrl) {
                const fallbackLink = document.getElementById('portal-link-fallback');
                if (fallbackLink && fallbackLink.href) {
                  portalUrl = fallbackLink.href;
                }
              }

              if (portalUrl) {
                if (window.personalised?.debugMode) console.log('Redirecting to Recharge portal:', portalUrl);
                try { window.personalised.storageManager.setSessionFresh(); } catch (e) {} // PR-4.3

                // Prevent spinner hide during navigation
                if (window.personalised?.modalSpinner?.setNavigating) {
                  window.personalised.modalSpinner.setNavigating(true);
                }
                navigating = true;

                // Always use web navigation for consistent same-WebView experience
                window.location.href = portalUrl;
              } else {
                console.error('Portal URL not found in any source (shadow, data-attr, fallback)');
                if (window.personalised?.debugMode) console.warn('Portal link URL resolution failed - no sources available');
              }
            });
          } catch (portalError) {
            console.error('Error redirecting to portal:', portalError);
          }
        } else {
          // Handle failure
          console.error('Failed to update bundle:', result.error);

          // Check for authentication errors
          if (window.personalised.utils?.handleAuthError) {
            window.personalised.utils.handleAuthError(result.error, 'handleSubmitButton');
          }

          // Optional alert for user feedback
          alert('There was an error updating your subscription. Please try again.');
        }
      } catch (err) {
        // Handle unexpected errors
        console.error('Error in bundle submit handler:', err);

        // Check for authentication errors
        if (window.personalised.utils?.handleAuthError) {
          window.personalised.utils.handleAuthError(err, 'handleSubmitButton');
        }
      } finally {
        // Only hide spinner and reset button if not navigating (error cases or navigation failures)
        // When navigating, let pagehide cleanup handle spinner removal
        if (!navigating) {
          if (window.personalised?.modalSpinner?.hide) {
            window.personalised.modalSpinner.hide();
          }
          // Reset button state
          if (submitButton) {
            submitButton.classList.remove('loading');
            submitButton.disabled = false;
          }
        }
      }
    }

    // =============================================================================
    // ===== SHARED — BUTTON MANAGEMENT =====
    // =============================================================================

    /**
     * [SHARED] Initialize checkout and update subscription buttons (desktop, mobile, and header).
     * Handles visibility and click-event binding based on current mode.
     */
    function initCheckoutButton() {
      const config = window.personalised.config;
      const wrapper = document.querySelector('#personalised-review-wrapper');

      if (!wrapper) {
        if (window.personalised?.debugMode) console.warn('Button initialization: Missing wrapper element');
        return;
      }

      // Get mode directly from data attribute or utils for safety
      let mode = wrapper.getAttribute('data-mode');
      if (!mode) {
        mode = window.personalised.utils.getMode();
        if (window.personalised.debugMode) console.log('Button initialization: No data-mode attribute found, using getMode():', mode);
      } else {
        if (window.personalised?.debugMode) console.log('Button initialization: Using data-mode attribute:', mode);
      }

      if (mode === 'edit') {
        // [EDIT ONLY] Force-hide checkout wrappers and ensure update-subscription is visible —
        // this provides a fast visual correction before the detailed visibility pass below.
        const desktopCheckoutWrap = document.getElementById('checkout-button-desktop-wrapper');
        const mobileCheckoutWrap  = document.getElementById('checkout-button-mobile-wrapper');
        const updateDesktopWrap   = document.getElementById('update-subscription-desktop-wrapper');
        const updateMobileWrap    = document.getElementById('update-subscription-mobile-wrapper');

        [desktopCheckoutWrap, mobileCheckoutWrap].forEach(el => {
          if (el) el.style.display = 'none';
        });
        // Ensure update subscription is visible
        [updateDesktopWrap, updateMobileWrap].forEach(el => {
          if (el) el.style.display = '';
        });

        // Optional: if checkout buttons were previously bound, ensure they remain inert in edit mode
        // (Do not remove listeners; just keep hidden)
      }

      const isEditMode = mode === config.modes.EDIT;
      const isGuestMode = mode === 'new' || mode === 'guest';
      if (window.personalised?.debugMode) console.log('Button visibility update:', isEditMode ? 'EDIT MODE' : 'GUEST/NEW MODE', 'Mode:', mode);

      // Update debug info
      if (wrapper) {
        wrapper.setAttribute('data-debug-mode', mode || 'unknown');
        if (window.personalised?.debugMode) console.debug('[Checkout Button Debug]', {
          mode: mode,
          viewport: window.innerWidth > 1200 ? 'desktop' : 'mobile',
          desktopButtonVisible: document.getElementById('checkout-button-desktop-wrapper')?.style.display !== 'none',
          mobileButtonVisible: document.getElementById('checkout-button-mobile-wrapper')?.style.display !== 'none'
        });
      }

      // Get checkout button elements
      const checkoutDesktopWrapper = document.getElementById('checkout-button-desktop-wrapper');
      const checkoutMobileWrapper = document.getElementById('checkout-button-mobile-wrapper');
      const checkoutHeaderWrapper = document.getElementById('checkout-button-header-wrapper');
      const checkoutDesktopButton = document.getElementById('checkout-button-desktop');
      const checkoutMobileButton = document.getElementById('checkout-button-mobile');
      const checkoutHeaderButton = document.getElementById('checkout-button-header');

      // Get update subscription button elements
      const updateDesktopWrapper = document.getElementById('update-subscription-desktop-wrapper');
      const updateMobileWrapper = document.getElementById('update-subscription-mobile-wrapper');
      const updateHeaderWrapper = document.getElementById('update-subscription-header-wrapper');
      const updateDesktopButton = document.getElementById('update-subscription-desktop');
      const updateMobileButton = document.getElementById('update-subscription-mobile');
      const updateHeaderButton = document.getElementById('update-subscription-header');

      // Update checkout button visibility (show only in guest/new mode)
      if (checkoutDesktopWrapper) checkoutDesktopWrapper.style.display = isGuestMode ? '' : 'none';
      if (checkoutMobileWrapper) checkoutMobileWrapper.style.display = isGuestMode ? '' : 'none';
      if (checkoutHeaderWrapper) checkoutHeaderWrapper.classList.toggle('visible', isGuestMode);

      // Update subscription button visibility (show only in edit mode)
      if (updateDesktopWrapper) updateDesktopWrapper.style.display = isEditMode ? '' : 'none';
      if (updateMobileWrapper) updateMobileWrapper.style.display = isEditMode ? '' : 'none';
      if (updateHeaderWrapper) updateHeaderWrapper.classList.toggle('visible', isEditMode);

      // Add data attributes to wrapper for CSS targeting
      if (wrapper) {
        wrapper.setAttribute('data-checkout-visible', isGuestMode ? 'true' : 'false');
        wrapper.setAttribute('data-update-visible', isEditMode ? 'true' : 'false');
      }

      // [GUEST/NEW ONLY] Click handler for checkout buttons — cart redirect or direct form POST.
      const handleCheckoutClick = function(event) {
        if (event && typeof event.preventDefault === 'function') event.preventDefault();
        const directCheckoutEnabled = event.currentTarget.getAttribute('data-direct-checkout') === 'true';

        if (!directCheckoutEnabled) {
          // Fallback: current behavior
          if (window.personalised?.debugMode) console.log('Checkout button clicked, redirecting to /cart');
          if (window.personalised && window.personalised.eventBus) {
            window.personalised.eventBus.emit('checkoutRedirectClicked', {
              timestamp: new Date().toISOString(),
              mode: mode,
              viewport: window.innerWidth > 1200 ? 'desktop' : 'mobile'
            });
          }
          window.location.href = window.personalised.config.routes.cart;
          return;
        }

        // Prevent double-submit
        if (event.currentTarget.disabled) return;
        event.currentTarget.disabled = true;

        if (window.personalised?.debugMode) console.log('Direct checkout initiated');

        // Analytics with direct checkout flag
        if (window.personalised && window.personalised.eventBus) {
          window.personalised.eventBus.emit('checkoutRedirectClicked', {
            timestamp: new Date().toISOString(),
            mode: mode,
            viewport: window.innerWidth > 1200 ? 'desktop' : 'mobile',
            directCheckout: true
          });
        }

        // Create and submit form
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = window.personalised.config.routes.cart;
        form.style.display = 'none';

        const checkoutInput = document.createElement('input');
        checkoutInput.type = 'hidden';
        checkoutInput.name = 'checkout';
        checkoutInput.value = '';
        form.appendChild(checkoutInput);

        document.body.appendChild(form);

        try {
          form.submit();
        } finally {
          setTimeout(() => {
            if (event.currentTarget) event.currentTarget.disabled = false;
            form.remove();
          }, 1000);
        }
      };

      // [GUEST/NEW ONLY] Bind checkout click handlers (only checkout buttons; update buttons are edit-only).
      [checkoutDesktopButton, checkoutMobileButton, checkoutHeaderButton].forEach(button => {
        if (button && !button.hasInitialized) {
          button.addEventListener('click', handleCheckoutClick);
          button.hasInitialized = true;
          if (window.personalised?.debugMode) console.log(`${button.id} event listener initialized`);
        }
      });

      // [EDIT ONLY] Bind update-subscription click handlers.
      [updateDesktopButton, updateMobileButton, updateHeaderButton].forEach(button => {
        if (button && !button.hasInitialized) {
          button.addEventListener('click', handleSubmitButton);
          button.hasInitialized = true;
          if (window.personalised?.debugMode) console.log(`${button.id} event listener initialized`);
        }
      });

    }

    /**
     * Manages checkout button positioning based on viewport size.
     * Places button inside the right column for desktop, outside columns for mobile.
     */
    function manageCheckoutButtonPosition() {
      const wrapper = document.querySelector('#personalised-review-wrapper');
      const rightColumn = document.querySelector('.personalised-review-right-wrapper');
      const checkoutContainer = document.getElementById('personalised-checkout-container');

      if (!wrapper || !rightColumn || !checkoutContainer) return;

      const isDesktop = window.matchMedia('(min-width: 1201px)').matches;

      if (isDesktop) {
        // For desktop: Move inside right column
        rightColumn.appendChild(checkoutContainer);
        checkoutContainer.classList.add('in-column');
      } else {
        // For mobile: Move outside columns (direct child of wrapper)
        wrapper.appendChild(checkoutContainer);
        checkoutContainer.classList.remove('in-column');
      }

      // After repositioning, check if we should display the button
      updateCheckoutButtonVisibility();
    }

    /**
     * Updates checkout and update subscription buttons visibility based on current mode.
     */
    function updateCheckoutButtonVisibility() {
      // Call initCheckoutButton which now handles both checkout and update subscription buttons
      initCheckoutButton();

      // Maintain container visibility logic for backward compatibility
      const wrapper = document.querySelector('#personalised-review-wrapper');
      const container = document.getElementById('personalised-checkout-container');

      if (!wrapper || !container) return;

      const mode = wrapper.getAttribute('data-mode') || window.personalised.utils.getMode();
      const isGuestMode = mode === 'new' || mode === 'guest';

      container.style.display = isGuestMode ? 'block' : 'none';
    }

    // =============================================================================
    // ===== SHARED — AFTERPAY AND DISCLAIMER =====
    // =============================================================================

    /**
     * [SHARED] Initialises Afterpay visibility based on the current bundle frequency.
     * Registers frequency-change radio listeners and responds to bundleStateLoaded events.
     * Idempotent — only registers listeners once per page load.
     */
    function initAfterPayVisibility() {
      if (initAfterPayVisibility._initialized) return;
      initAfterPayVisibility._initialized = true;

      const afterpayElement = document.querySelector('.personalised-review-afterpay');
      const afterpayText = document.querySelector('.personalised-review-afterpay-text');
      const afterpayRow = document.querySelector('.personalised-review-payment-option');
      if (!afterpayElement || !afterpayText) return;

      const shouldShowAfterpay = window.personalised?.config?.shouldShowAfterpay;
      console.log('Afterpay visibility check:', shouldShowAfterpay);
      if (typeof shouldShowAfterpay !== 'function' || !shouldShowAfterpay({ source: 'review_status_text' })) {
        afterpayElement.classList.add('disabled');
        afterpayText.textContent = '';
        if (afterpayRow) afterpayRow.style.display = 'none';
        return;
      }
      
      console.log('Afterpay is enabled and available for this bundle', afterpayRow);

      if (afterpayRow) afterpayRow.style.display = '';

      function updateAfterPayStatus(frequency) {
        if (frequency === '30') {
          afterpayElement.classList.add('disabled');
          afterpayText.textContent = 'only available for 2 or 3 monthly pack orders';
        } else {
          afterpayElement.classList.remove('disabled');
          afterpayText.textContent = 'available as payment option';
        }
      }

      // Listen for frequency changes via radio buttons
      document.querySelectorAll('input[name="bundle-frequency"]').forEach(radio => {
        radio.addEventListener('change', function() {
          updateAfterPayStatus(this.value);
        });
      });

      // Re-evaluate when bundle state is loaded or reloaded
      window.personalised.eventBus.on('bundleStateLoaded', function() {
        const frequency = window.personalised.bundle.getFrequency?.();
        if (frequency) updateAfterPayStatus(frequency);
      });

      // Immediate update using the current bundle frequency (state already loaded by this point)
      const frequency = window.personalised.bundle.getFrequency?.();
      if (frequency) updateAfterPayStatus(frequency);
    }

    async function waitForSyncOutcome(timeoutMs = 20000) {
      return new Promise(resolve => {
        const bus = window.personalised?.eventBus;
        if (!bus?.on) {
          resolve({ ok: false, reason: 'no_event_bus' });
          return;
        }

        let settled = false;

        const cleanup = () => {
          if (!bus?.off) return;
          bus.off('syncCompleted', onCompleted);
          bus.off('syncFailed', onFailed);
        };

        const finish = payload => {
          if (settled) return;
          settled = true;
          cleanup();
          resolve(payload);
        };

        const onCompleted = () => finish({ ok: true });
        const onFailed = event => finish({ ok: false, reason: 'sync_failed', event });

        bus.on('syncCompleted', onCompleted);
        bus.on('syncFailed', onFailed);

        setTimeout(() => {
          finish({ ok: false, reason: 'sync_timeout' });
        }, timeoutMs);
      });
    }

    function initOneOffPurchaseControls() {
      const toggle = document.querySelector('[data-oneoff-purchase-toggle]');
      if (!toggle) return;

      if (initOneOffPurchaseControls._initialized) {
        initOneOffPurchaseControls.refresh?.();
        return;
      }
      initOneOffPurchaseControls._initialized = true;

      const frequencyGroup = document.getElementById('bundle-frequency-group');
      const frequencyContainer = frequencyGroup?.closest('.personalised-review-frequency');
      const oneOffButtons = Array.from(toggle.querySelectorAll('[data-oneoff-switch-months]'));
      const switchBackLink = toggle.querySelector('[data-oneoff-switchback]');
      const frequencyInputs = Array.from(document.querySelectorAll('input[name="bundle-frequency"]'));
      const checkoutControls = Array.from(document.querySelectorAll(
        '#checkout-button-desktop, #checkout-button-mobile, [data-oneoff-modal-trigger]'
      ));
      let reviewSelectionInFlight = false;

      const updateSelectionUI = () => {
        const purchaseType = window.personalised.bundle.getPurchaseType?.() || 'subscription';
        const monthsValue = Number(window.personalised.bundle.getOneOffMonths?.());
        const isOneOff = purchaseType === 'one-off' && [4, 6].includes(monthsValue);

        toggle.hidden = !isOneOff;
        if (frequencyContainer) {
          frequencyContainer.hidden = isOneOff;
        }

        oneOffButtons.forEach(button => {
          const buttonMonths = Number(button.dataset.oneoffSwitchMonths);
          const isSelected = isOneOff && buttonMonths === monthsValue;
          button.classList.toggle('is-selected', isSelected);
          button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });
      };

      const setReviewControlsDisabled = (disabled) => {
        oneOffButtons.forEach(button => {
          button.disabled = disabled;
        });
        if (switchBackLink) switchBackLink.disabled = disabled;
        frequencyInputs.forEach(input => {
          input.disabled = disabled;
        });
        checkoutControls.forEach(control => {
          control.disabled = disabled;
        });
      };

      const syncSelectionChange = async (message) => {
        if (typeof window.personalised.utils?.debouncedRefreshCartFromState === 'function' && window.personalised?.eventBus?.on) {
          const syncWait = waitForSyncOutcome();
          window.personalised.utils.debouncedRefreshCartFromState(message);
          const outcome = await syncWait;
          return outcome.ok === true ? { ok: true } : { ok: false, outcome };
        }

        if (typeof window.personalised.utils?.refreshCartFromState === 'function') {
          const syncedItems = await window.personalised.utils.refreshCartFromState(message);
          return { ok: Array.isArray(syncedItems) };
        }

        return { ok: false, outcome: { reason: 'no_sync_api' } };
      };

      const restoreSubscriptionSelection = () => {
        window.personalised.bundle?.setPurchaseType?.('subscription');
        window.personalised.bundleUI?.renderBundleItems?.();
        window.personalised.bundleUI?.updatePriceDisplay?.();
        window.personalised.eventBus?.emit?.('renderComplete');
        updateSelectionUI();
      };

      const applyReviewSelection = async (type, months = null) => {
        if (reviewSelectionInFlight) return;
        reviewSelectionInFlight = true;

        setReviewControlsDisabled(true);

        const setResult = type === 'one-off'
          ? window.personalised.bundle?.setPurchaseType?.('one-off', Number(months))
          : window.personalised.bundle?.setPurchaseType?.('subscription');

        if (!setResult) {
          reviewSelectionInFlight = false;
          setReviewControlsDisabled(false);
          updateSelectionUI();
          return;
        }

        window.personalised.bundleUI?.renderBundleItems?.();
        window.personalised.bundleUI?.updatePriceDisplay?.();
        window.personalised.eventBus?.emit?.('renderComplete');
        updateSelectionUI();

        let syncOk = false;
        let handledFailure = false;
        try {
          const syncMessage = type === 'one-off'
            ? '✅ One-off selected'
            : '✅ Subscription restored';
          const result = await syncSelectionChange(syncMessage);
          syncOk = result.ok === true;

          if (!syncOk) {
            const errorMessage = String(result?.outcome?.event?.error?.message || result?.outcome?.event?.message || '').toLowerCase();
            if (errorMessage.includes('selling plan')) {
              handledFailure = true;
              restoreSubscriptionSelection();
              window.personalised.utils?.debouncedRefreshCartFromState?.('Reverting to subscription');
              alert('These products currently require a subscription selling plan. Please use subscription checkout, or configure one-time purchasable variants for one-off.');
            }
          }
        } catch (error) {
          syncOk = false;
          if (window.personalised?.debugMode) console.warn('[OneOffControls] Failed to sync selection change', error);
        } finally {
          reviewSelectionInFlight = false;
          setReviewControlsDisabled(false);
          updateSelectionUI();
        }

        if (!syncOk && !handledFailure) {
          alert('We could not update your cart right now. Please try again in a moment.');
        }
      };

      oneOffButtons.forEach(button => {
        button.addEventListener('click', function() {
          const selectedMonths = Number(button.dataset.oneoffSwitchMonths);
          const currentType = window.personalised.bundle.getPurchaseType?.() || 'subscription';
          const currentMonths = Number(window.personalised.bundle.getOneOffMonths?.());
          if (currentType === 'one-off' && currentMonths === selectedMonths) return;
          applyReviewSelection('one-off', selectedMonths);
        });
      });

      switchBackLink?.addEventListener('click', function() {
        const currentType = window.personalised.bundle.getPurchaseType?.() || 'subscription';
        if (currentType === 'subscription') return;
        applyReviewSelection('subscription');
      });

      window.personalised.eventBus?.on?.('bundleStateLoaded', updateSelectionUI);
      window.addEventListener('pageshow', updateSelectionUI);
      initOneOffPurchaseControls.refresh = updateSelectionUI;
      updateSelectionUI();
    }

    function initOneOffPurchaseModal() {
      if (initOneOffPurchaseModal._initialized) return;
      initOneOffPurchaseModal._initialized = true;

      const features = window.personalised?.config?.features;
      const featureEnabled = !!features?.oneOffPurchaseEnabled;
      const triggers = Array.from(document.querySelectorAll('[data-oneoff-modal-trigger]'));
      const modal = document.getElementById('personalised-oneoff-modal');
      if (!triggers.length || !modal) return;
      const accordionButtons = Array.from(modal.querySelectorAll('[data-oneoff-accordion-toggle]'));
      const mediaQuery = window.matchMedia('(max-width: 1200px)');
      let expandedMonths = null;
      let mobileScrollRestoreY = 0;

      const mode = window.personalised?.utils?.getMode?.();
      const allowModalMode = mode === 'guest' || mode === 'new';

      const setTriggersVisible = (visible) => {
        triggers.forEach(trigger => {
          trigger.hidden = !visible;
          const mount = trigger.closest('[data-enhance-afterpay-target], [data-enhance-afterpay-target-mobile]');
          if (mount) mount.hidden = !visible;
        });
      };

      if (!allowModalMode || !featureEnabled) {
        setTriggersVisible(false);
        return;
      }

      setTriggersVisible(true);

      function isMobileOneOffLayout() {
        return mediaQuery.matches;
      }

      function getDefaultExpandedMonths() {
        const selectedMonths = Number(window.personalised.bundle?.getOneOffMonths?.());
        if ([4, 6].includes(selectedMonths)) {
          return selectedMonths;
        }
        return 4;
      }

      function syncAccordionUI() {
        if (![4, 6].includes(Number(expandedMonths))) {
          expandedMonths = getDefaultExpandedMonths();
        }

        accordionButtons.forEach(button => {
          const monthsValue = Number(button.dataset.oneoffAccordionToggle);
          const isExpanded = monthsValue === Number(expandedMonths);
          const card = button.closest('[data-oneoff-card]');
          const panel = modal.querySelector(`[data-oneoff-panel="${monthsValue}"]`);

          button.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
          button.classList.toggle('is-expanded', isExpanded);
          if (card) {
            card.classList.toggle('is-expanded', isExpanded);
          }
          if (panel) {
            panel.hidden = !isExpanded;
          }
        });
      }

      function lockMobileViewport() {
        if (!isMobileOneOffLayout()) return;
        mobileScrollRestoreY = window.scrollY || window.pageYOffset || 0;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${mobileScrollRestoreY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
      }

      function unlockMobileViewport() {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        if (mobileScrollRestoreY) {
          window.scrollTo(0, mobileScrollRestoreY);
        }
        mobileScrollRestoreY = 0;
      }

      const openModal = function() {
        expandedMonths = getDefaultExpandedMonths();
        syncAccordionUI();
        modal.hidden = false;
        modal.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('personalised-oneoff-modal-open');
        document.body.classList.add('personalised-oneoff-modal-open');
        if (isMobileOneOffLayout()) {
          document.documentElement.classList.add('personalised-oneoff-modal-mobile-open');
          document.body.classList.add('personalised-oneoff-modal-mobile-open');
          lockMobileViewport();
        }

        const dialog = modal.querySelector('.personalised-review-oneoff-modal__dialog');
        if (dialog) dialog.scrollTop = 0;

        refreshOneOffModalPricing();
      };

      const closeModal = function() {
        modal.hidden = true;
        modal.setAttribute('aria-hidden', 'true');
        document.documentElement.classList.remove('personalised-oneoff-modal-open');
        document.body.classList.remove('personalised-oneoff-modal-open');
        document.documentElement.classList.remove('personalised-oneoff-modal-mobile-open');
        document.body.classList.remove('personalised-oneoff-modal-mobile-open');
        if (mobileScrollRestoreY || isMobileOneOffLayout()) {
          unlockMobileViewport();
        }
      };

      const handleTriggerClick = function(event) {
        event.preventDefault();
        openModal();
      };

      const handleTriggerKeydown = function(event) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openModal();
        }
      };

      triggers.forEach(trigger => {
        trigger.hidden = false;
        trigger.addEventListener('click', handleTriggerClick);
        trigger.addEventListener('keydown', handleTriggerKeydown);
      });

      modal.querySelectorAll('[data-oneoff-modal-close]').forEach(control => {
        control.addEventListener('click', function(event) {
          event.preventDefault();
          closeModal();
        });
      });

      window.addEventListener('keydown', function(event) {
        if (event.key === 'Escape' && !modal.hidden) {
          closeModal();
        }
      });

      modal.addEventListener('click', function(event) {
        if (event.target === modal) {
          closeModal();
        }
      });

      accordionButtons.forEach(button => {
        button.addEventListener('click', function() {
          const monthsValue = Number(button.dataset.oneoffAccordionToggle);
          if (![4, 6].includes(monthsValue)) return;
          if (expandedMonths === monthsValue) return;
          expandedMonths = monthsValue;
          syncAccordionUI();
        });
      });

      mediaQuery.addEventListener('change', function() {
        if (!modal.hidden) {
          syncAccordionUI();
          if (isMobileOneOffLayout()) {
            document.documentElement.classList.add('personalised-oneoff-modal-mobile-open');
            document.body.classList.add('personalised-oneoff-modal-mobile-open');
            lockMobileViewport();
          } else {
            document.documentElement.classList.remove('personalised-oneoff-modal-mobile-open');
            document.body.classList.remove('personalised-oneoff-modal-mobile-open');
            unlockMobileViewport();
          }
        }
      });

      async function fetchCartData() {
        try {
          const response = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
          if (!response.ok) return null;
          return await response.json();
        } catch (error) {
          if (window.personalised?.debugMode) console.warn('[OneOffModal] Failed to fetch cart for pricing preview', error);
          return null;
        }
      }

      function getBundleSubtotalCents() {
        const bundle = window.personalised?.bundle;
        const items = bundle?.getItems?.() || [];
        if (!items.length) return 0;

        if (window.personalised.bundleUI?.ensureVariantLookupBuilt) {
          window.personalised.bundleUI.ensureVariantLookupBuilt();
        }

        const lookup = window.personalised.config?.variantLookup || {};
        return items.reduce((sum, item) => {
          const record = lookup[String(item.variantId)];
          const price = record?.variant?.price_cents;
          if (typeof price !== 'number') return sum;
          return sum + price * Number(item.quantity || 1);
        }, 0);
      }

      function getOrderLevelRate(cartData) {
        const itemsSubtotalCents = cartData?.items_subtotal_price || 0;
        const cartOrderDiscountCents = (cartData?.cart_level_discount_applications || [])
          .reduce((sum, discount) => sum + (discount.total_allocated_amount || 0), 0);
        return itemsSubtotalCents > 0
          ? Math.min(1, Math.max(0, cartOrderDiscountCents / itemsSubtotalCents))
          : 0;
      }

      function formatPrice(cents) {
        return window.personalised?.priceCalculator?.formatPrice
          ? window.personalised.priceCalculator.formatPrice(cents)
          : `$${(Math.max(0, cents) / 100).toFixed(2)}`;
      }

      function getDiscountCode(cartData) {
        const application = (cartData?.cart_level_discount_applications || [])[0];
        return (application?.title || application?.code || '').trim();
      }

      function renderPreviewCard(months, cartData) {
        const subtotalCents = getBundleSubtotalCents();
        const monthsValue = Number(months);
        const rateByTier = monthsValue === 4 ? 0.20 : 0.25;
        const tierBeforeDiscount = subtotalCents * monthsValue;
        const tierAfterTierDiscount = Math.round(tierBeforeDiscount * (1 - rateByTier));
        const orderLevelRate = getOrderLevelRate(cartData);
        const tierAfterAllDiscounts = Math.round(tierAfterTierDiscount * (1 - orderLevelRate));
        const savingAmount = Math.max(0, tierBeforeDiscount - tierAfterAllDiscounts);
        const perMonthAmount = monthsValue > 0 ? Math.round(tierAfterAllDiscounts / monthsValue) : 0;

        const beforeEl = modal.querySelector(`[data-oneoff-price-before="${monthsValue}"]`);
        const afterEl = modal.querySelector(`[data-oneoff-price-after="${monthsValue}"]`);
        const savingEl = modal.querySelector(`[data-oneoff-saving="${monthsValue}"]`);
        const perMonthEl = modal.querySelector(`[data-oneoff-per-month="${monthsValue}"]`);
        const cardEl = modal.querySelector(`[data-oneoff-card="${monthsValue}"]`);

        if (beforeEl) beforeEl.textContent = formatPrice(tierBeforeDiscount);
        if (afterEl) afterEl.textContent = formatPrice(tierAfterAllDiscounts);
        if (savingEl) savingEl.textContent = `Saving ${formatPrice(savingAmount)}`;
        if (perMonthEl) perMonthEl.textContent = `${formatPrice(perMonthAmount)} per month`;
        if (cardEl) {
          cardEl.classList.toggle('is-selected', Number(window.personalised.bundle?.getOneOffMonths?.()) === monthsValue);
        }

        return {
          tierAfterAllDiscounts,
          tierBeforeDiscount,
          savingAmount,
          perMonthAmount,
          afterpayInstallment: Math.round(tierAfterAllDiscounts / 4)
        };
      }

      function refreshOneOffModalPricing() {
        const discountLine = modal.querySelector('[data-oneoff-discount-line]');
        const discountCodeEl = modal.querySelector('[data-oneoff-discount-code]');
        const afterpayCopyEl = modal.querySelector('[data-oneoff-afterpay-copy]');

        // Optimistic local pass — avoids a $0.00 flash while the cart-discount fetch resolves.
        renderPreviewCard(4, null);
        renderPreviewCard(6, null);

        fetchCartData().then(cartData => {
          const preview4 = renderPreviewCard(4, cartData);
          const preview6 = renderPreviewCard(6, cartData);
          syncAccordionUI();

          if (discountLine) {
            discountLine.hidden = true;
          }
          if (discountCodeEl) {
            discountCodeEl.textContent = getDiscountCode(cartData);
          }

          if (afterpayCopyEl) {
            const previewValues = [preview4, preview6].filter(Boolean);
            const lowestPreview = previewValues.reduce((lowest, preview) => {
              if (!lowest) return preview;
              return preview.tierAfterAllDiscounts < lowest.tierAfterAllDiscounts ? preview : lowest;
            }, null);
            const installment = lowestPreview ? lowestPreview.afterpayInstallment : 0;
            afterpayCopyEl.innerHTML = `
              Afterpay is available at checkout when you buy one-off.<br />
              4 interest-free payments from ${formatPrice(installment)}.
            `;
          }
        });
      }

      let oneOffSelectionInFlight = false;

      async function selectOneOffMonths(months) {
        if (oneOffSelectionInFlight) return;
        oneOffSelectionInFlight = true;

        const choiceButtons = modal.querySelectorAll('[data-oneoff-months]');
        choiceButtons.forEach(button => {
          button.disabled = true;
        });

        const selectedMonths = Number(months);
        expandedMonths = selectedMonths;
        syncAccordionUI();
        const setResult = window.personalised.bundle?.setPurchaseType?.('one-off', selectedMonths);
        if (!setResult) {
          oneOffSelectionInFlight = false;
          choiceButtons.forEach(button => {
            button.disabled = false;
          });
          return;
        }

        // Reflect one-off choice immediately in review UI before network sync.
        if (window.personalised.bundleUI?.renderBundleItems) {
          window.personalised.bundleUI.renderBundleItems();
        }

        if (window.personalised.bundleUI?.updatePriceDisplay) {
          window.personalised.bundleUI.updatePriceDisplay();
        }
        window.personalised.eventBus?.emit?.('renderComplete');
        initOneOffPurchaseControls.refresh?.();

        closeModal();

        let syncOk = false;
        let handledFailure = false;
        try {
          if (window.personalised?.modalSpinner?.show) {
            window.personalised.modalSpinner.show('Saving your pack...');
          }

          if (typeof window.personalised.utils?.debouncedRefreshCartFromState === 'function' && window.personalised?.eventBus?.on) {
            const syncWait = waitForSyncOutcome();
            window.personalised.utils.debouncedRefreshCartFromState('✅ One-off selected');
            const outcome = await syncWait;
            syncOk = outcome.ok === true;
            if (!syncOk) {
              const errorMessage = String(outcome?.event?.error?.message || outcome?.event?.message || '').toLowerCase();
              if (errorMessage.includes('selling plan')) {
                handledFailure = true;
                window.personalised.bundle?.setPurchaseType?.('subscription');
                window.personalised.bundleUI?.renderBundleItems?.();
                window.personalised.bundleUI?.updatePriceDisplay?.();
                initOneOffPurchaseControls.refresh?.();
                window.personalised.utils?.debouncedRefreshCartFromState?.('Reverting to subscription');
                alert('These products currently require a subscription selling plan. Please use subscription checkout, or configure one-time purchasable variants for one-off.');
                return;
              }
            }
          } else if (typeof window.personalised.utils?.refreshCartFromState === 'function') {
            const syncedItems = await window.personalised.utils.refreshCartFromState('✅ One-off selected');
            syncOk = Array.isArray(syncedItems);
          }
        } catch (error) {
          syncOk = false;
          if (window.personalised?.debugMode) console.warn('[OneOffModal] Failed to sync one-off selection', error);
        } finally {
          if (syncOk) {
            if (window.personalised?.modalSpinner?.setNavigating) {
              window.personalised.modalSpinner.setNavigating(true);
            }
          } else if (window.personalised?.modalSpinner?.hide) {
            window.personalised.modalSpinner.hide();
          }
          oneOffSelectionInFlight = false;
          choiceButtons.forEach(button => {
            button.disabled = false;
          });
        }

        if (syncOk) {
          window.location.href = '/checkout';
        } else if (!handledFailure) {
          alert('We could not update your cart right now. Please try again in a moment.');
        }
      }

      modal.querySelectorAll('[data-oneoff-months]').forEach(button => {
        button.addEventListener('click', function() {
          selectOneOffMonths(button.dataset.oneoffMonths);
        });
      });
    }

    /**
     * [GUEST/NEW MODE ONLY] Shows checkout disclaimers for non-edit modes.
     * Never called in edit mode — edit mode uses the update-subscription button, not checkout.
     * Called early in initReviewPage so disclaimers appear as soon as mode is known.
     * @param {string} mode - The current bundle mode.
     */
    function initDisclaimerVisibility(mode) {
      if (mode !== 'edit') {
        document.getElementById('checkout-disclaimer-desktop')?.style.setProperty('display', 'block');
        document.getElementById('checkout-disclaimer-mobile')?.style.setProperty('display', 'block');
      }
    }

    // Public API
    return {
      handleSubmitButton,
      initCheckoutButton,
      initOneOffPurchaseModal,
      manageCheckoutButtonPosition,
      updateCheckoutButtonVisibility,
      initAfterPayVisibility,
      initOneOffPurchaseControls,
      initDisclaimerVisibility
    };
  })();

  window.personalised.reviewActions = reviewActions;
})();
